import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createTicketTemplate,
  deleteTicketTemplates,
  getAllTicketTemplates,
  getTicketTemplateById,
  updateTicketTemplate,
} from "controller/api";
import {
  ApiErrorType,
  CreateTicketTemplateInput,
  GetQueryProps,
  UpdateTicketTemplateInput,
} from "shared";
import { sentryLogger } from "utilz";

export const useGetAllTicketTemplates = ({
  perPageCount,
  pageNumber,
  sortColumn,
  sortDirection,
  filter,
  searchQuery,
}: GetQueryProps) => {
  return useQuery({
    queryKey: ["ticketTemplates", searchQuery],
    queryFn: () =>
      getAllTicketTemplates({
        perPageCount,
        pageNumber,
        sortColumn,
        sortDirection,
        filter,
        searchQuery,
      }),
    // enabled: searchQuery && searchQuery?.length > 1 ? true : false,
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "customer",
        functionName: "getAllTicketTemplates",
      });
    },
  });
};

export const useGetTicketTemplateByIdQuery = (templateID: string) => {
  return useQuery({
    queryKey: ["ticketTemplate", templateID],
    queryFn: () => getTicketTemplateById(templateID),
    enabled: templateID ? true : false,
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "customer",
        functionName: "getTicketTemplateById",
      });
    },
  });
};

export const useGetTicketTemplateByIdMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (templateId: string) => getTicketTemplateById(templateId),
    {
      onSuccess(data, variables, context) {
        console.log(data);
        queryClient.invalidateQueries(["ticketTemplate", variables]);
      },
      onError: (error: ApiErrorType, variables, context) => {
        sentryLogger({
          error: error,
          data: variables,
          workflow: "customer",
          level: "error",
          functionName: "getTicketTemplateById",
        });
      },
    }
  );
};

export const useCreateTicketTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: CreateTicketTemplateInput) => createTicketTemplate(data),
    {
      onSuccess(data, variables, context) {
        queryClient.invalidateQueries(["ticketTemplates"]);
      },
      onError: (error: ApiErrorType, variables, context) => {
        sentryLogger({
          error: error,
          data: variables,
          workflow: "customer",
          level: "error",
          functionName: "createTicketTemplate",
        });
      },
    }
  );
};

export const useUpdateTicketTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: UpdateTicketTemplateInput) => updateTicketTemplate(data),
    {
      onSuccess(data, variables, context) {
        queryClient.invalidateQueries(["ticketTemplates"]);
      },
      onError: (error: ApiErrorType, variables, context) => {
        sentryLogger({
          error: error,
          data: variables,
          workflow: "customer",
          level: "error",
          functionName: "updateTicketTemplate",
        });
      },
    }
  );
};

export const useDeleteTicketTemplates = () => {
  const queryClient = useQueryClient();
  return useMutation(deleteTicketTemplates, {
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries(["ticketTemplates"]);
    },
    onError: (error: ApiErrorType, variables, context) => {
      sentryLogger({
        error: error,
        data: variables,
        workflow: "customer",
        level: "error",
        functionName: "deleteTicketTemplates",
      });
    },
  });
};
